import React from 'react'
import { format } from 'date-fns'
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react'
import { StandardColumn } from '../../../../components/StandardPage'
import { getMovementDirection } from '../../../../utils/formatMovementQuantity'

export const productionMovementScreenKey = 'production_movement'

const CELL_EMPTY = '-'

interface User {
  name: string
}

export interface FashionProductionMovementRow {
  id: number
  ploomesOrderId: number
  rawMaterialName: string
  status: string
  quantity: string | number
  oldTotalWeight: number
  newTotalWeight: number
  oldLengthInMm: number
  newLengthInMm: number
  oldQuantity: number
  newQuantity: number
  createdAt: string
  orderName?: string
  observation?: string
  user?: User
  updatedBy?: User
  updatedAt?: string
  stockId?: number | null
  fashionConsumedStockId?: number | null
  fashionConsumedResourceId?: number | null
  isBuyOrder?: boolean
  buyOrderStatus?: string
  unitOfMeasurement?: string
  [key: string]: unknown
}

export function fashionMovementStatusCode(status: unknown): string {
  return String(status ?? '').trim()
}

export function isFashionMovementReverted(item: { status?: unknown }): boolean {
  const s = fashionMovementStatusCode(item.status)
  return s === '410' || s.startsWith('410')
}

export function fashionMovementStatusLabel(item: { status?: unknown }): string {
  const s = fashionMovementStatusCode(item.status)
  if (isFashionMovementReverted(item)) return 'Desfeito'
  if (s === 'Editado' || s === 'EDITADO' || s.toLowerCase().includes('editad')) return 'Editado'
  return 'Abatido'
}

export function parseQuantitySuffix(q: unknown): { num: number; suffix: string } | null {
  const s = String(q ?? '').trim()
  const m = s.match(/^([\d.,]+)\s*([a-zA-Zºªá-úÁ-Ú%./\-*]+.*)$/)
  if (!m) return null
  const num = parseFloat(m[1].replace(',', '.'))
  if (!Number.isFinite(num)) return null
  return { num, suffix: m[2].trim() }
}

function isMeterLengthSuffix(suffix: string): boolean {
  return suffix.toUpperCase() === 'M'
}

function isMillimeterSuffix(suffix: string): boolean {
  return suffix.toUpperCase() === 'MM'
}

function isPieceUnitSuffix(suffix: string): boolean {
  const u = suffix.toUpperCase()
  return u === 'UN' || u === 'UNIDADE' || u === 'UN.'
}

export function displayFashionMovementQuantity(
  item: FashionProductionMovementRow,
  formatNum: (v: number | null | undefined) => string,
  isSteel?: boolean,
): string {
  if (item.isBuyOrder) return String(item.quantity ?? CELL_EMPTY)
  const rawNum = Number(item.quantity)
  if (!isNaN(rawNum)) {
    const formatted = rawNum % 1 === 0
      ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(rawNum)
      : formatNum(rawNum)
    const unit = item.unitOfMeasurement || ''
    if (unit) {
      return isSteel ? `${formatted}${unit.toLowerCase()}` : `${formatted} ${unit.toUpperCase()}`
    }
    return formatted
  }
  const parsed = parseQuantitySuffix(item.quantity)
  if (!parsed) return String(item.quantity ?? CELL_EMPTY)
  const { num, suffix } = parsed
  if (isSteel) {
    const formatted = num % 1 === 0
      ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num)
      : formatNum(num)
    return `${formatted}${suffix.toLowerCase()}`
  }
  if (isMillimeterSuffix(suffix)) return `${formatNum(num)} MM`
  if (!isMeterLengthSuffix(suffix)) {
    const qty = isPieceUnitSuffix(suffix) ? String(Math.round(num)) : formatNum(num)
    return `${qty} ${suffix.toUpperCase()}`
  }
  const deltaLen = (item.oldLengthInMm ?? 0) - (item.newLengthInMm ?? 0)
  if (deltaLen > 1e-6) {
    if (Math.abs(num - deltaLen) < 1) return `${formatNum(num / 1000)} M`
    if (Math.abs(num * 1000 - deltaLen) < 1) return `${formatNum(num)} M`
  }
  return `${formatNum(num)} M`
}

export function displayFashionLengths(
  item: FashionProductionMovementRow,
  formatNum: (v: number | null | undefined) => string,
): { old: string; new: string } {
  const o = item.oldLengthInMm
  const n = item.newLengthInMm
  const parsed = parseQuantitySuffix(item.quantity)
  const sfx = parsed?.suffix ?? ''
  const hasNumericLength =
    (o != null && Math.abs(o) > 1e-9) ||
    (n != null && Math.abs(n) > 1e-9) ||
    (o != null && n != null && Math.abs(o - n) > 1e-6)
  if (!hasNumericLength) return { old: CELL_EMPTY, new: CELL_EMPTY }
  if (isMeterLengthSuffix(sfx) && !isMillimeterSuffix(sfx) && o != null && n != null) {
    return {
      old: `${formatNum(o / 1000)} M`,
      new: `${formatNum(n / 1000)} M`,
    }
  }
  return {
    old: `${formatNum(o)} MM`,
    new: `${formatNum(n)} MM`,
  }
}

export function displayFashionWeights(
  item: FashionProductionMovementRow,
  formatNum: (v: number | null | undefined) => string,
): { old: string; new: string } {
  return {
    old: `${formatNum(item.oldTotalWeight)} KG`,
    new: `${formatNum(item.newTotalWeight)} KG`,
  }
}

export function fashionOpIdFromOrderName(orderName: string | null | undefined): number | null {
  const str = String(orderName ?? '')
  const m = str.match(/^OP-FASHION-(\d+)\|/) || str.match(/^OPF-(\d+)/)
  return m ? Number(m[1]) : null
}

export function insumoResourceIdFromRaw(raw: string): number | null {
  const m = /^__INS_RES_(\d+)__$/.exec(raw)
  return m ? Number(m[1]) : null
}

export function materialCodeFromRaw(raw: string): string {
  return raw.replace(/__\d+$/, '').trim()
}

export function displayFashionMaterialLabel(
  raw: string,
  insumoNames: Record<number, string>,
): string {
  const ins = insumoResourceIdFromRaw(raw)
  if (ins != null) return insumoNames[ins] ?? `Insumo #${ins}`
  return materialCodeFromRaw(raw)
}

export function formatFashionUnitQuantity(value: number | null | undefined): string {
  if (value == null || typeof value !== 'number') return CELL_EMPTY
  return String(Math.round(value))
}

interface MovementColumnsOptions {
  isSteel: boolean
  pedidoByOpId: Record<number, string>
  insumoNameById: Record<number, string>
  formatNumber: (value: number | undefined | null) => string
  onMaterialClick: (item: FashionProductionMovementRow) => void
}

const getOriginLabelRaw = (
  item: FashionProductionMovementRow,
  isSteel: boolean,
  pedidoByOpId: Record<number, string>,
): string => {
  const opId = fashionOpIdFromOrderName(String(item.orderName ?? ''))
  const pedidoLabel = opId != null ? pedidoByOpId[opId] ?? CELL_EMPTY : CELL_EMPTY
  return isSteel
    ? (item.ploomesOrderId && item.ploomesOrderId > 1
        ? (item.orderName ? String(item.orderName) : 'Pedido')
        : 'Manual')
    : (pedidoLabel !== CELL_EMPTY ? pedidoLabel : 'Manual')
}

const clickableSpanStyle: React.CSSProperties = {
  color: '#0090d3',
  fontWeight: 'bold',
  cursor: 'pointer',
}

export const buildFashionMovementColumns = ({
  isSteel,
  pedidoByOpId,
  insumoNameById,
  formatNumber,
  onMaterialClick,
}: MovementColumnsOptions): StandardColumn<FashionProductionMovementRow>[] => [
  {
    id: 'direction',
    label: 'Tipo',
    sortable: false,
    exportValue: (item) => {
      if (!isSteel && item.isBuyOrder) return 'Ordem de Compra'
      const dir = (item as any).direction || getMovementDirection(item)
      return dir === 'IN' ? 'Entrada' : 'Saída'
    },
    cell: (item) => {
      const isBuy = !isSteel && item.isBuyOrder
      const dir = (item as any).direction || getMovementDirection(item)
      const isInput = dir === 'IN'
      const directionLabel = isBuy ? 'Ordem de Compra' : (isInput ? 'Entrada' : 'Saída')

      let icon = null
      if (isBuy) {
        icon = <ArrowUpCircle color="#0090d3" size={20} />
      } else if (isInput) {
        icon = <ArrowUpCircle color="green" size={20} />
      } else {
        icon = <ArrowDownCircle color="red" size={20} />
      }

      return (
        <span title={directionLabel} style={{ display: 'inline-flex' }}>
          {icon}
        </span>
      )
    },
  },
  {
    id: 'origin',
    label: 'Origem',
    accessor: (item) => getOriginLabelRaw(item, isSteel, pedidoByOpId),
    cell: (item) => {
      const opId = fashionOpIdFromOrderName(String(item.orderName ?? ''))
      const pedidoLabel = opId != null ? pedidoByOpId[opId] ?? CELL_EMPTY : CELL_EMPTY
      const originLabelRaw = getOriginLabelRaw(item, isSteel, pedidoByOpId)
      const originLabel = originLabelRaw.length > 25 ? originLabelRaw.slice(0, 25) + '...' : originLabelRaw
      const title = (item as any).observation || (isSteel
        ? (item.orderName ? String(item.orderName) : undefined)
        : (pedidoLabel !== CELL_EMPTY ? pedidoLabel : undefined))

      if (isSteel && item.ploomesOrderId && item.ploomesOrderId > 1) {
        return (
          <span title={title}>
            <span
              onClick={() => {
                window.open(`https://app10.ploomes.com/deal/${item.ploomesOrderId}`, '_blank')
              }}
              style={clickableSpanStyle}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
            >
              {item.orderName && String(item.orderName).length > 25
                ? String(item.orderName).slice(0, 25) + '...'
                : item.orderName || 'Pedido'}
            </span>
          </span>
        )
      }
      return <span title={title}>{originLabel}</span>
    },
  },
  {
    id: 'rawMaterialName',
    label: 'Código',
    accessor: (item) =>
      isSteel
        ? String(item.rawMaterialName ?? '')
        : displayFashionMaterialLabel(String(item.rawMaterialName ?? ''), insumoNameById),
    cell: (item) => (
      <span
        onClick={() => onMaterialClick(item)}
        style={clickableSpanStyle}
        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      >
        {isSteel
          ? String(item.rawMaterialName ?? '')
          : displayFashionMaterialLabel(String(item.rawMaterialName ?? ''), insumoNameById)}
      </span>
    ),
  },
  {
    id: 'quantity',
    label: 'Movimento',
    accessor: (item) => displayFashionMovementQuantity(item, formatNumber, isSteel),
    cell: (item) => displayFashionMovementQuantity(item, formatNumber, isSteel),
  },
  {
    id: 'oldTotalWeight',
    label: 'Peso anterior',
    cell: (item) => displayFashionWeights(item, formatNumber).old,
  },
  {
    id: 'newTotalWeight',
    label: 'Peso novo',
    cell: (item) => displayFashionWeights(item, formatNumber).new,
  },
  {
    id: 'oldLengthInMm',
    label: 'Comp. anterior',
    cell: (item) => displayFashionLengths(item, formatNumber).old,
  },
  {
    id: 'newLengthInMm',
    label: 'Comp. novo',
    cell: (item) => displayFashionLengths(item, formatNumber).new,
  },
  {
    id: 'oldQuantity',
    label: 'Qtd. anterior',
    cell: (item) => formatFashionUnitQuantity(item.oldQuantity),
  },
  {
    id: 'newQuantity',
    label: 'Qtd. nova',
    cell: (item) => formatFashionUnitQuantity(item.newQuantity),
  },
  {
    id: 'status',
    label: 'Status',
    accessor: (item) => (item.isBuyOrder ? item.buyOrderStatus || '' : fashionMovementStatusLabel(item)),
    cell: (item) => (item.isBuyOrder ? item.buyOrderStatus : fashionMovementStatusLabel(item)),
  },
  {
    id: 'createdAt',
    label: 'Data',
    accessor: (item) => new Date(item.createdAt).getTime(),
    cell: (item) => `${format(new Date(item.createdAt), 'dd/MM/yyyy - HH:mm')}h`,
  },
]
